// Utility to detect and validate current sitename on the client
// - Parses hostname to extract left-most label as candidate sitename
// - Handles localhost, azurewebsites.net suppression, and caching (5 minutes)
// - Calls /api/sitenames/validate to verify against reserved list and sitenames collection

export type SitenameValidation = {
  ok: boolean
  status?: 'pending' | 'active' | 'approved' | 'rejected' | 'none'
  ownerId?: string
  value?: string
}

const CACHE_KEY = 'sitename_validation_cache_v1'
const CACHE_TTL_MS = 5 * 60 * 1000 // 5 minutes

function readCache(hostname: string): SitenameValidation | null {
  try {
    if (typeof window === 'undefined') return null
    const raw = sessionStorage.getItem(CACHE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Record<string, { ts: number; val: SitenameValidation }>
    const entry = parsed?.[hostname]
    if (!entry) return null
    if (Date.now() - entry.ts > CACHE_TTL_MS) return null
    return entry.val
  } catch {
    return null
  }
}

function writeCache(hostname: string, val: SitenameValidation) {
  try {
    if (typeof window === 'undefined') return
    const raw = sessionStorage.getItem(CACHE_KEY)
    const data = raw ? (JSON.parse(raw) as any) : {}
    data[hostname] = { ts: Date.now(), val }
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(data))
  } catch {}
}

export function parseHostname(hostname: string): {
  isLocalhost: boolean
  isAzureWebsites: boolean
  labels: string[]
  baseDomain: string | null
  candidate: string | null
} {
  // Normalize and strip port if present (e.g., firstibo.localhost:3000)
  // This function may be called on the server with req.headers.get('host'),
  // which commonly includes a :port suffix in dev.
  const hostWithPort = String(hostname || '').toLowerCase().trim()
  const host = hostWithPort.replace(/:\d+$/, '')
  const labels = host.split('.')
  const isLocalhost = host === 'localhost' || host.endsWith('.localhost')
  const isAzureWebsites = host.endsWith('.azurewebsites.net')

  let baseDomain: string | null = null
  if (isAzureWebsites && labels.length >= 3) {
    // e.g., sub.site.azurewebsites.net -> baseDomain: site.azurewebsites.net
    baseDomain = labels.slice(-3).join('.')
  } else if (isLocalhost) {
    // Handle localhost and *.localhost specially to ensure base is plain 'localhost'
    // e.g., bob.localhost -> baseDomain: 'localhost'
    baseDomain = 'localhost'
  } else if (labels.length >= 2) {
    // General case: last two labels (e.g., example.com)
    baseDomain = labels.slice(-2).join('.')
  }

  const firstLabel: string | null = labels.length > 0 ? (labels[0] ?? null) : null
  const candidate: string | null =
    labels.length >= 3
      ? firstLabel
      : isLocalhost && labels.length >= 2
      ? firstLabel
      : null
  return { isLocalhost, isAzureWebsites, labels, baseDomain, candidate }
}

export async function validateCurrentSitename(): Promise<{
  hostname: string
  baseDomain: string | null
  candidate: string | null
  validation: SitenameValidation
}> {
  if (typeof window === 'undefined') {
    return { hostname: '', baseDomain: null, candidate: null, validation: { ok: false, status: 'none' } }
  }
  const hostname = window.location.hostname
  const { baseDomain, candidate } = parseHostname(hostname)

  const cached = readCache(hostname)
  if (cached) return { hostname, baseDomain, candidate, validation: cached }

  try {
    if (!candidate) {
      const res = { ok: false, status: 'none' as const }
      writeCache(hostname, res)
      return { hostname, baseDomain, candidate, validation: res }
    }
    const url = new URL('/api/sitenames/validate', window.location.origin)
    url.searchParams.set('value', candidate)
    const resp = await fetch(url.toString(), { method: 'GET', headers: { 'Accept': 'application/json' }, cache: 'no-store' })
    if (!resp.ok) {
      // Fail-open best practice: do not redirect on API errors
      const res = { ok: true, status: 'none' as const } // treat as OK to avoid breaking pages
      writeCache(hostname, res)
      return { hostname, baseDomain, candidate, validation: res }
    }
    const data = (await resp.json()) as SitenameValidation
    const normalized: SitenameValidation = {
      ok: Boolean(data?.ok),
      status: (data?.status as any) || 'none',
      ownerId: data?.ownerId,
      value: data?.value || candidate,
    }
    writeCache(hostname, normalized)
    return { hostname, baseDomain, candidate, validation: normalized }
  } catch {
    // Network failure: fail-open
    const res = { ok: true, status: 'none' as const }
    writeCache(hostname, res)
    return { hostname, baseDomain, candidate, validation: res }
  }
}

export function buildDiscoverUrlForCurrentHost(sitename: string | null): string {
  const name = (sitename || '').trim().toLowerCase()
  if (!name) return 'https://discoveryourdrive.com/'
  return `https://${name}.discoveryourdrive.com/`
}
